/**
 * pbServer — the per-request server client of the Next.js adapter.
 *
 * Every call builds a FRESH runtime over the request's cookie store and
 * returns a runtime-BOUND `PB` client: typed namespaces, `call`, `upload`
 * and `auth` all resolve that runtime, never the process-global slot — two
 * bound clients serving concurrent requests cannot leak each other's
 * sessions (see createBoundClient). The store itself is read lazily per
 * adapter operation, so nothing is snapshotted beyond the call.
 *
 * MODULE-GRAPH RULE (same as ./index.ts): 'next/headers' is imported ONLY
 * lazily inside the default-store path — importing this module, and using
 * pbServer with an injected store, must work without next installed.
 */
import { environmentRefFromPublishableApiKey } from '../api-key.js';
import { BackendError } from '../errors.js';
import { buildRuntime, createBoundClient } from '../internal.js';
import type { PB } from '../pb.js';
import type { PersistedSession, SessionStorageAdapter } from '../storage.js';
import {
  clearedSessionCookieNames,
  decodeSessionCookies,
  encodeSessionCookiesDecoded,
  SESSION_COOKIE_ATTRS,
} from './cookie-codec.js';
import { requireGlobalConfig } from './global-config.js';
import { oauthCookieStorage } from './oauth-storage.js';

/**
 * Structural slice of Next's request cookie store (`await cookies()`); any
 * object honoring it works — the seam for tests and non-Next servers.
 * `set`/`delete` are optional because RSC-scope stores are read-only.
 */
export interface CookieStoreLike {
  get(name: string): { name: string; value: string } | undefined;
  getAll?(): Array<{ name: string; value: string }>;
  set?(name: string, value: string, options?: Record<string, unknown>): void;
  delete?(name: string): void;
}

function nextRequired(cause: unknown): BackendError {
  const detail = cause instanceof Error && cause.message ? ` (${cause.message})` : '';
  return new BackendError('validation', {
    code: 'next_required',
    message:
      'pbServer() could not resolve a Next.js request cookie store: call it inside a Server Component, Server Action or Route Handler — or pass a store explicitly via pbServer({ cookies })' +
      `${detail}.`,
  });
}

/**
 * Next CONTROL-FLOW errors must pass through UNWRAPPED. During static
 * prerender (`next build`) `cookies()` throws DynamicServerError (digest
 * 'DYNAMIC_SERVER_USAGE'), which Next catches upstream to mark the route
 * dynamic — wrapping it into a BackendError would turn that bailout signal
 * into a real prerender error and FAIL THE BUILD of every page calling
 * pbServer (caught live in the P3 Next smoke). 'NEXT_'-prefixed digests are
 * the same sentinel family (redirect / not-found / static-gen bailout).
 * Genuine outside-request-scope errors carry no digest and still map to
 * next_required.
 */
export function isNextControlFlowError(err: unknown): boolean {
  if (typeof err !== 'object' || err === null) return false;
  const digest = (err as { digest?: unknown }).digest;
  return (
    typeof digest === 'string' && (digest === 'DYNAMIC_SERVER_USAGE' || digest.startsWith('NEXT_'))
  );
}

/** Lazy default store: `await (await import('next/headers')).cookies()`. */
async function nextCookieStore(): Promise<CookieStoreLike> {
  let cookies: () => Promise<CookieStoreLike>;
  try {
    ({ cookies } = await import('next/headers'));
  } catch (cause) {
    throw nextRequired(cause); // next not installed / not resolvable
  }
  try {
    return await cookies();
  } catch (cause) {
    if (isNextControlFlowError(cause)) throw cause; // static-prerender bailout etc.
    throw nextRequired(cause); // installed, but outside a request scope
  }
}

/** Delete one cookie: set-with-maxAge-0 when the store can set (parity with the browser side), else delete, else swallow (read-only RSC store). */
function removeCookie(store: CookieStoreLike, name: string): void {
  try {
    if (store.set) store.set(name, '', { ...SESSION_COOKIE_ATTRS, maxAge: 0 });
    else store.delete?.(name);
  } catch {
    // RSC read-only store — cookie mutation is the middleware's job there
  }
}

/**
 * SessionStorageAdapter over the request cookie store.
 *
 * SAVE SEMANTICS (decision, documented): only FULL sessions (access token +
 * expiry present) are written; a refresh-only save is SKIPPED with no throw
 * rather than written as {a:'',e:0}. The cookie is SHARED with the browser —
 * degrading it would drop an access token the browser may still be using and
 * force a refresh per page-load. The refresh-only branch is a defensive
 * legacy path anyway: every successful refresh saves a full session.
 *
 * All writes swallow per-cookie failures: in RSC scope Next's store throws
 * on mutation; the request still proceeds with the in-memory session but the
 * rotated cookie is NOT persisted. palbeProxy is therefore REQUIRED —
 * not optional — for session-bearing RSC apps: without it every RSC render
 * re-refreshes from the SAME cookie refresh token, and once two such
 * refreshes land more than 30s apart (outside palauth's rotation grace) the
 * reuse detector REVOKES THE WHOLE TOKEN FAMILY — the user is
 * force-logged-out everywhere.
 *
 * Values are written DECODED (encodeSessionCookiesDecoded): Next's real
 * cookies() store percent-encodes on serialization, yielding exactly the
 * shared-codec wire bytes; a raw store (tests, custom servers) persists the
 * bare JSON, which the load path's decode tolerates identically (tokens are
 * %-free base64url, so decodeURIComponent is the identity on them).
 */
function serverCookieAdapter(
  store: CookieStoreLike,
  environmentRef: string,
): SessionStorageAdapter {
  const present = (name: string): boolean => store.get(name) !== undefined;
  return {
    load(): PersistedSession | null {
      const stored = decodeSessionCookies((name) => store.get(name)?.value, environmentRef);
      if (!stored) return null;
      // A browser-side refresh-only write round-trips as a:'' / e:0 —
      // normalize to the legacy shape so hydration takes the expired-trick.
      return stored.accessToken && stored.expiresAt > 0
        ? {
            refreshToken: stored.refreshToken,
            accessToken: stored.accessToken,
            expiresAt: stored.expiresAt,
          }
        : { refreshToken: stored.refreshToken };
    },
    save(session: PersistedSession): void {
      if (!session.accessToken || !session.expiresAt) return; // see SAVE SEMANTICS above
      for (const name of clearedSessionCookieNames(environmentRef, present)) {
        removeCookie(store, name);
      }
      const { set, clear } = encodeSessionCookiesDecoded(environmentRef, {
        accessToken: session.accessToken,
        refreshToken: session.refreshToken,
        expiresAt: session.expiresAt,
      });
      for (const { name, value } of set) {
        try {
          store.set?.(name, value, { ...SESSION_COOKIE_ATTRS });
        } catch {
          // RSC read-only store
        }
      }
      // Overflow guard (codec contract): delete one-past-the-end so a stale
      // orphan chunk behind a gap can never join a future chunk run.
      for (const name of clear) removeCookie(store, name);
    },
    clear(): void {
      for (const name of clearedSessionCookieNames(environmentRef, present)) {
        removeCookie(store, name);
      }
    },
  };
}

/**
 * Build a per-request `PB` client over the request's cookies. Reads the
 * session the browser SDK wrote (shared cookie codec), refreshes pre-flight
 * when expired, and — where the store is writable (Server Actions, Route
 * Handlers) — persists the rotated session back.
 *
 * Each call builds a fresh runtime (a handful of small objects, no I/O) —
 * cheap enough to call per data access; code paths invoking it many times
 * within one request MAY memoize per-request (e.g. React's `cache()`), but
 * don't have to.
 *
 * ```ts
 * const pb = await pbServer();
 * const todos = await pb.todos.list();
 * ```
 */
export async function pbServer(opts?: { cookies?: CookieStoreLike }): Promise<PB> {
  const config = requireGlobalConfig(
    'import the generated palbe.gen.ts once in app/layout.tsx — the root-layout import configures Server Components and Route Handlers too.',
  );
  const store = opts?.cookies ?? (await nextCookieStore());
  const ref = environmentRefFromPublishableApiKey(config.apiKey);
  const rt = buildRuntime(
    { ...config, storage: serverCookieAdapter(store, ref) },
    { oauthStorage: oauthCookieStorage(store) },
  );
  return createBoundClient(rt);
}
